import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { Campaign, CampaignRecipient, CreateVariantInput } from '@/types/newsletter'

interface CampaignListResponse {
  data: Campaign[]
  pagination: { page: number; per_page: number; total: number }
}

interface CampaignRecipientsResponse {
  data: CampaignRecipient[]
  pagination: { page: number; per_page: number; total: number }
}

interface CreateCampaignRequest {
  audience_id: string
  name: string
  subject: string
  from_address: string
  reply_to?: string
  template_id?: string
  html_content?: string
  text_content?: string
  template_data?: Record<string, unknown>
  variants?: CreateVariantInput[]
}

interface UpdateCampaignRequest {
  audience_id?: string
  name?: string
  subject?: string
  from_address?: string
  reply_to?: string
  template_id?: string
  html_content?: string
  text_content?: string
  template_data?: Record<string, unknown>
  variants?: CreateVariantInput[]
}

interface SendCampaignResponse {
  id: string
  status: 'sending'
  total_recipients: number
  skipped: number
}

interface ScheduleCampaignRequest {
  campaign_id: string
  scheduled_at: string
}

interface CampaignListParams {
  page?: number
  per_page?: number
  status?: string
}

interface RecipientListParams {
  page?: number
  per_page?: number
  status?: string
}

const QUERY_KEY = ['campaigns'] as const
const RECIPIENTS_KEY = ['campaign-recipients'] as const

export function useCampaigns(
  params?: CampaignListParams,
): ReturnType<typeof useQuery<CampaignListResponse>> {
  return useQuery({
    queryKey: [...QUERY_KEY, params],
    queryFn: () => apiClient.get<CampaignListResponse>(endpoints.campaigns.list(params)),
  })
}

export function useCampaign(id: string): ReturnType<typeof useQuery<Campaign>> {
  return useQuery({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => apiClient.get<Campaign>(endpoints.campaigns.detail(id)),
    enabled: !!id,
  })
}

export function useCreateCampaign(): ReturnType<
  typeof useMutation<Campaign, unknown, CreateCampaignRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCampaignRequest) =>
      apiClient.post<Campaign>(endpoints.campaigns.create, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useUpdateCampaign(): ReturnType<
  typeof useMutation<Campaign, unknown, { id: string; data: UpdateCampaignRequest }>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCampaignRequest }) =>
      apiClient.put<Campaign>(endpoints.campaigns.update(id), data),
    onSuccess: (_result, { id }) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, id] })
    },
  })
}

export function useDeleteCampaign(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(endpoints.campaigns.delete(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useSendCampaign(): ReturnType<
  typeof useMutation<SendCampaignResponse, unknown, string>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<SendCampaignResponse>(endpoints.campaigns.send(id), {}),
    onSuccess: (_result, id) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, id] })
    },
  })
}

export function useScheduleCampaign(): ReturnType<
  typeof useMutation<Campaign, unknown, ScheduleCampaignRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ campaign_id, scheduled_at }: ScheduleCampaignRequest) =>
      apiClient.post<Campaign>(endpoints.campaigns.schedule(campaign_id), { scheduled_at }),
    onSuccess: (_result, { campaign_id }) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, campaign_id] })
    },
  })
}

export function useCancelCampaign(): ReturnType<typeof useMutation<Campaign, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.post<Campaign>(endpoints.campaigns.cancel(id), {}),
    onSuccess: (_result, id) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, id] })
    },
  })
}

interface SendWinnerRequest {
  campaignId: string
  variantId: string
}

interface SendWinnerResponse {
  id: string
  status: string
}

export function useSendWinner(): ReturnType<
  typeof useMutation<SendWinnerResponse, unknown, SendWinnerRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ campaignId, variantId }: SendWinnerRequest) =>
      apiClient.post<SendWinnerResponse>(endpoints.campaigns.sendWinner(campaignId), {
        variant_id: variantId,
      }),
    onSuccess: (_result, { campaignId }) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, campaignId] })
    },
  })
}

export function useCampaignRecipients(
  id: string,
  params?: RecipientListParams,
): ReturnType<typeof useQuery<CampaignRecipientsResponse>> {
  return useQuery({
    queryKey: [...RECIPIENTS_KEY, id, params],
    queryFn: () =>
      apiClient.get<CampaignRecipientsResponse>(endpoints.campaigns.recipients(id, params)),
    enabled: !!id,
  })
}

export type {
  CampaignListParams,
  CampaignListResponse,
  CampaignRecipientsResponse,
  CreateCampaignRequest,
  RecipientListParams,
  ScheduleCampaignRequest,
  SendCampaignResponse,
  SendWinnerRequest,
  SendWinnerResponse,
  UpdateCampaignRequest,
}
